import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInvestorKYC } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ManageKYCTable/hook/useInvestorKYC';
import { useQuery } from '@tanstack/react-query';
import { getMipKYCData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { usePaginationWithSearch } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/hook/usePaginationWithSearch';
import { KYC_PAGINATION_PARAMS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/constants';

// depending on where the the kyc review page is navigated from, we need to fetch the investors from the paginated list or the single investor
// ie is a kyc review being done on a paginated list of investors or a single investor
export const useInvestor = () => {
	const { investorId } = useParams();
	const [searchParams] = useSearchParams();
	const reviewPaginatedInvestors = searchParams.get('reviewPaginatedInvestors') === 'true';
	const { pagination, searchQuery } = usePaginationWithSearch({
		defaultOffset: KYC_PAGINATION_PARAMS.DEFAULT_OFFSET,
		defaultLimit: KYC_PAGINATION_PARAMS.DEFAULT_LIMIT,
	});
	const getMipKYCDataRequest = useQuery({
		queryKey: [
			API_PATHS.MIP_KYC_INVESTORS_GET,
			{
				searchQuery,
				offset: pagination.state.offset,
				limit: pagination.state.limit,
			},
		],
		queryFn: ({ signal }) =>
			getMipKYCData(
				API_PATHS.MIP_KYC_INVESTORS_GET,
				{
					searchQuery,
					offset: pagination.state.offset,
					limit: pagination.state.limit,
				},
				signal
			),
		enabled: reviewPaginatedInvestors,
	});

	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const getInvestorKYCDataRequest = useInvestorKYC(true, investorId, kycWorkflowId);
	const currentInvestor = useMemo(() => {
		// the paginated data can be stale after kyc updates so get the latest investor data
		// depending where the kyc review page was navigated from the investor data does not always exist
		const currentInvestor = getInvestorKYCDataRequest.data?.payload?.investors?.find(
			(investor) => investor.id === investorId
		);
		return currentInvestor;
	}, [getInvestorKYCDataRequest.data?.payload?.investors, investorId]);

	const paginatedInvestors = useMemo(() => {
		// Return paginated investors if available
		if (reviewPaginatedInvestors === true) {
			return getMipKYCDataRequest.data?.payload?.investors ?? [];
		}
		// Otherwise return single investor as a list
		return getInvestorKYCDataRequest.data?.payload?.investors ?? [];
	}, [
		getMipKYCDataRequest.data?.payload?.investors,
		getInvestorKYCDataRequest.data?.payload?.investors,
		reviewPaginatedInvestors,
	]);

	return {
		currentInvestor,
		paginatedInvestors,
		isLoading:
			getInvestorKYCDataRequest.isLoading ||
			(reviewPaginatedInvestors && getMipKYCDataRequest.isLoading),
	};
};
